/**
 * IndexedDB initializer - lightweight, dependency-free
 * Handles database creation, versioning, and recovery
 */

const DEFAULT_CONFIG = {
  dbName: 'kissandra-r2-cache',
  dbVersion: 6,
  stores: {
    characters: 'characters',
    characterDetails: 'character-details',
    scenarios: 'scenarios',
    tagMappings: 'tag-mappings',
    scenarioImages: 'scenarioImages',
    imageUrls: 'imageUrls',
    locations: 'locations'
  },
  maxUpgradeAttempts: 2
};

export class IndexedDBInitializer {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.db = null;
    this.isInitialized = false;
    this.initializationPromise = null;
    this.upgradeAttempts = 0;
    this.hasLoggedInProgress = false;
  }

  async init() {
    if (this.initializationPromise) {
      if (!this.hasLoggedInProgress) {
        console.log('IndexedDB: init in progress');
        this.hasLoggedInProgress = true;
      }
      return this.initializationPromise;
    }

    if (this.isInitialized && this.db) {
      const missing = this.checkMissingStores();
      if (missing.length === 0) {
        this.initializationPromise = Promise.resolve(this.db);
        return this.initializationPromise;
      }

      this.db.close();
      this.db = null;
      this.isInitialized = false;
      
      if (this.upgradeAttempts < this.config.maxUpgradeAttempts) {
        this.upgradeAttempts++;
        this.initializationPromise = this.forceUpgrade().then(() => this.init());
        return this.initializationPromise;
      }
      
      this.initializationPromise = Promise.resolve(null);
      return this.initializationPromise;
    }

    const currentVersion = await this.getCurrentVersion();
    if (currentVersion > this.config.dbVersion) {
      this.config.dbVersion = currentVersion;
    }

    this.initializationPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.config.dbName, this.config.dbVersion);

      request.onerror = (event) => {
        if (event.target.error.name === 'VersionError' && this.upgradeAttempts < this.config.maxUpgradeAttempts) {
          this.upgradeAttempts++;
          this.forceUpgrade().then(() => this.init()).then(resolve).catch(reject);
          return;
        }
        
        this.initializationPromise = Promise.resolve(null);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        
        const missing = this.checkMissingStores();
        if (missing.length > 0 && this.upgradeAttempts < this.config.maxUpgradeAttempts) {
          this.upgradeAttempts++;
          this.forceUpgrade().then(() => this.init()).then(resolve).catch(reject);
          return;
        }

        this.isInitialized = true;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        this.createStores(event.target.result);
      };
    });

    return this.initializationPromise;
  }

  createStores(db) {
    const { stores } = this.config;
    const storeConfigs = [
      { name: stores.characters, keyPath: 'id', indexes: ['updatedAt'] },
      { name: stores.characterDetails, keyPath: 'id', indexes: ['updatedAt'] },
      { name: stores.scenarios, keyPath: 'id', indexes: ['updatedAt', 'tokenExpiry'] },
      { name: stores.tagMappings, keyPath: 'key', indexes: ['updatedAt'] },
      { name: stores.scenarioImages, keyPath: 'id', indexes: ['updatedAt', 'tokenExpiry'] },
      { name: stores.imageUrls, keyPath: 'key', indexes: ['updatedAt'] },
      { name: stores.locations, keyPath: 'id', indexes: ['updatedAt'] },
      { name: 'metadata', keyPath: 'key' }
    ];

    storeConfigs.forEach(({ name, keyPath, indexes = [] }) => {
      if (!db.objectStoreNames.contains(name)) {
        const store = db.createObjectStore(name, { keyPath });
        indexes.forEach(index => store.createIndex(index, index, { unique: false }));
      }
    });
  }

  checkMissingStores() {
    if (!this.db) return ['No database connection'];
    
    const required = [
      this.config.stores.tagMappings,
      this.config.stores.scenarioImages,
      this.config.stores.imageUrls,
      this.config.stores.locations
    ];
    
    return required.filter(name => !this.db.objectStoreNames.contains(name));
  }

  async getCurrentVersion() {
    return new Promise((resolve) => {
      const request = indexedDB.open(this.config.dbName);
      
      request.onsuccess = (event) => {
        const version = event.target.result.version;
        event.target.result.close();
        resolve(version);
      };
      
      request.onerror = () => resolve(0);
      request.onupgradeneeded = (event) => {
        event.target.result.close();
        resolve(0);
      };
    });
  }

  async forceUpgrade() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    
    this.isInitialized = false;
    this.initializationPromise = null;

    const currentVersion = await this.getCurrentVersion();
    this.config.dbVersion = Math.max(currentVersion + 1, this.config.dbVersion + 1);

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.config.dbName, this.config.dbVersion);

      request.onerror = (event) => reject(event.target.error);
      request.onupgradeneeded = (event) => this.createStores(event.target.result);
      request.onsuccess = (event) => {
        this.db = event.target.result;
        this.isInitialized = true;
        resolve();
      };
    });
  }

  getDatabase() {
    return this.db;
  }

  isReady() {
    return this.isInitialized && this.db !== null;
  }

  getStores() {
    return this.config.stores;
  }
}
